import React, { use, useCallback, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { AuthManagerContext } from "../../contexts/AuthManager";
import Gap from "../../ui_atoms/Gap";
import Button from "../../components/Button";
import { useI18n } from "../../hooks/i18n-hooks";
import ScreenCard from "../ScreenCard";
import Switch from "../../ui_atoms/Switch";
import SelectField from "../../components/SelectField";
import { useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useAsyncError } from "../../hooks/application";
import VStack from "../../ui_atoms/VStack";
import Text from "../../components/Text";

type ScreenParams = StackScreenProps<RootStackParamList>;

const ConfigureLppScreen = () => {
    const i18n = useI18n();
    const throwError = useAsyncError();
    const navigator = useNavigation<ScreenParams["navigation"]>();
    const { refreshUser } = use(AuthManagerContext);

    const [hasFullWorkingCapacity, setHasFullWorkingCapacity] = useState(false);
    const [hasNo3MonthsIncapacityInLast12Months, setHasNo3MonthsIncapacityInLast12Months] =
        useState(false);
    const [hasNoIncapacityNow, setHasNoIncapacityNow] = useState(false);
    const [hasReceivedDisabilityPensionInLast3Years, setHasReceivedDisabilityPensionInLast3Years] =
        useState(false);
    const [hasAcceptedAll, setHasAcceptedAll] = useState(false);

    const [percentPension, setPercentPension] = useState<string>();
    const [percentDisability, setPercentDisability] = useState<string>();

    const onSubmit = useCallback(() => {
        try {
            //TODO need to submitt regisration LPP data
            void refreshUser?.();
            navigator.goBack();
        } catch (e) {
            throwError(e as Error);
        }
    }, [refreshUser, throwError, navigator]);

    const percentPensionsData = useMemo(
        () =>
            ["25", "50", "75", "100"].map((percent) => ({
                value: percent,
                label: i18n.t(`screens.configure_lpp.${percent}percentPension`),
            })),
        [i18n],
    );

    const percentDisabilityData = useMemo(
        () =>
            ["10", "20", "30", "40", "50", "60", "70", "80", "90", "100"].map((percent) => ({
                value: percent,
                label: i18n.t(`screens.configure_lpp.percentOfDisability`, { percent }),
            })),
        [i18n],
    );

    const isValid = useMemo(
        () =>
            hasFullWorkingCapacity &&
            hasNo3MonthsIncapacityInLast12Months &&
            hasNoIncapacityNow &&
            (!hasReceivedDisabilityPensionInLast3Years ||
                (hasReceivedDisabilityPensionInLast3Years &&
                    !!percentPension &&
                    !!percentDisability)) &&
            hasAcceptedAll,
        [
            hasAcceptedAll,
            hasFullWorkingCapacity,
            hasNo3MonthsIncapacityInLast12Months,
            hasNoIncapacityNow,
            hasReceivedDisabilityPensionInLast3Years,
            percentDisability,
            percentPension,
        ],
    );

    return (
        <>
            <ScreenCard
                buttons={[
                    {
                        title: i18n.t("screens.configure_lpp.submit_request"),
                        onPress: onSubmit,
                        isValid: isValid,
                    },
                ]}
            >
                <VStack v={1} gap={1} h="2/3">
                    <Text>{i18n.t("screens.configure_lpp.intro")}</Text>
                    <Button
                        size="large"
                        buttonMode="outlined"
                        label={i18n.t("screens.configure_lpp.see_plan")}
                        onPress={() => {
                            navigator.navigate("PdfViewer", {
                                assetId: "", //TODO need to add asset id
                            });
                        }}
                    />
                    <Text>{i18n.t("screens.configure_lpp.please_confirm")}</Text>
                    <View style={styles.optionLine}>
                        <Switch
                            value={hasFullWorkingCapacity}
                            onChange={() => {
                                setHasFullWorkingCapacity(!hasFullWorkingCapacity);
                            }}
                        />
                        <Text style={styles.optionText}>
                            {i18n.t("screens.configure_lpp.has_full_working_capacity")}
                        </Text>
                    </View>
                    <View style={styles.optionLine}>
                        <Switch
                            value={hasNo3MonthsIncapacityInLast12Months}
                            onChange={() => {
                                setHasNo3MonthsIncapacityInLast12Months(
                                    !hasNo3MonthsIncapacityInLast12Months,
                                );
                            }}
                        />
                        <Text style={styles.optionText}>
                            {i18n.t(
                                "screens.configure_lpp.has_no_3_months_incapacity_in_last_12_months",
                            )}
                        </Text>
                    </View>
                    <View style={styles.optionLine}>
                        <Switch
                            value={hasNoIncapacityNow}
                            onChange={() => {
                                setHasNoIncapacityNow(!hasNoIncapacityNow);
                            }}
                        />
                        <Text style={styles.optionText}>
                            {i18n.t("screens.configure_lpp.has_no_incapacity_now")}
                        </Text>
                    </View>
                    <View style={styles.optionLine}>
                        <Switch
                            value={hasReceivedDisabilityPensionInLast3Years}
                            onChange={() => {
                                setHasReceivedDisabilityPensionInLast3Years(
                                    !hasReceivedDisabilityPensionInLast3Years,
                                );
                            }}
                        />
                        <Text style={styles.optionText}>
                            {i18n.t(
                                "screens.configure_lpp.has_received_disability_pension_in_last_3_years",
                            )}
                        </Text>
                    </View>
                    {hasReceivedDisabilityPensionInLast3Years && (
                        <>
                            <SelectField
                                value={
                                    percentPensionsData.find(
                                        (item) => item.value === percentPension,
                                    )?.value
                                }
                                onSelect={({ value }) => {
                                    setPercentPension(value);
                                }}
                                placeholder={i18n.t("screens.configure_lpp.pension_level")}
                                data={percentPensionsData}
                            />
                            <Gap gap={0.5} />
                            <SelectField
                                value={
                                    percentDisabilityData.find(
                                        (item) => item.value === percentDisability,
                                    )?.value
                                }
                                onSelect={({ value }) => {
                                    setPercentDisability(value);
                                }}
                                placeholder={i18n.t("screens.configure_lpp.disability_level")}
                                data={percentDisabilityData}
                            />
                            <Gap gap={0.5} />
                        </>
                    )}
                    <View style={styles.optionLine}>
                        <Switch
                            value={hasAcceptedAll}
                            onChange={() => {
                                setHasAcceptedAll(!hasAcceptedAll);
                            }}
                        />
                        <Text style={styles.optionText}>
                            {i18n.t("screens.configure_lpp.has_accepted_all")}
                        </Text>
                    </View>
                </VStack>
            </ScreenCard>
        </>
    );
};

const styles = StyleSheet.create({
    optionsSelector: {
        display: "flex",
        flexDirection: "column",
        alignSelf: "stretch",
    },
    optionLine: {
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        paddingVertical: 8,
    },
    optionText: {
        marginLeft: 0,
        flex: 1,
    },
});

export default ConfigureLppScreen;
